import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, ShoppingCart } from 'lucide-react';
import { ProductImageCarousel } from '../../components/customer/ProductImageCarousel';
import { UnavailableBadge, isUnavailable, isVariantUnavailable } from '../../components/customer/UnavailableBadge';
import { ComingSoonButton } from '../../components/customer/ComingSoonButton';
import { isComingSoon } from '../../utils/comingSoon';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user, storeSettings } = useAuth();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const curr = storeSettings?.currency_symbol || '₹';
  const autoPlay = storeSettings?.auto_image_carousel !== false;

  useEffect(() => {
    const loadProduct = async () => {
      try {
        let adminId = user?.admin_id;
        if (!adminId && storeSettings?.slug) {
          const tenantRes = await axios.get(`${API}/store/by-slug/${storeSettings.slug}`);
          adminId = tenantRes.data?.admin_id;
        }
        if (!adminId) return;
        const res = await axios.get(`${API}/public/store/${adminId}/products`);
        const all = res.data || [];
        const found = all.find(p => p.id === productId);
        setProduct(found || null);
        if (found?.category_id) {
          setRelatedProducts(all.filter(p => p.category_id === found.category_id && p.id !== found.id).slice(0, 8));
        }
      } catch (e) {
        console.error('Failed to load product', e);
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
  }, [productId, user?.admin_id, storeSettings?.slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--theme-bg)] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--theme-primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[var(--theme-bg)] flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-[var(--theme-text-muted)]">Product not found</p>
        <button onClick={() => navigate(-1)} className="px-4 py-2 bg-[var(--theme-primary)] text-white rounded-lg text-sm">Go Back</button>
      </div>
    );
  }

  return <ProductDetailContent product={product} relatedProducts={relatedProducts} curr={curr} autoPlay={autoPlay} storeSettings={storeSettings} />;
};

const ProductDetailContent = ({ product, relatedProducts, curr, autoPlay, storeSettings }) => {
  const navigate = useNavigate();
  const { addItem, getItemQuantity, updateQuantity, cartItems = [] } = useCart();

  const hasVariants = product.variants && product.variants.length > 0;
  const defaultVariant = hasVariants ? (product.variants.find(v => v.is_default) || product.variants[0]) : null;
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
  const productOos = isUnavailable(product, storeSettings);
  const comingSoon = isComingSoon(product, storeSettings);

  const activeVariant = selectedVariant || defaultVariant;
  const displayPrice = activeVariant ? activeVariant.price : product.price;
  const displayMrp = activeVariant ? activeVariant.mrp : product.mrp;
  const discount = displayMrp > displayPrice ? Math.round((1 - displayPrice / displayMrp) * 100) : 0;
  const qty = hasVariants
    ? (activeVariant ? getItemQuantity(product.id, activeVariant.label) : 0)
    : getItemQuantity(product.id);
  const cartKey = hasVariants && activeVariant ? `${product.id}__${activeVariant.label}` : product.id;
  const allImages = useMemo(() => [...new Set([product.image_url, ...(product.images || [])].filter(Boolean))], [product]);
  const totalCartItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleAdd = () => {
    if (hasVariants && activeVariant) {
      addItem(product, 1, activeVariant);
    } else {
      addItem(product, 1);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--theme-bg)] flex flex-col" data-testid="product-detail-page">

      {/* ── Fixed Header ── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[var(--theme-bg)] border-b border-[var(--theme-border)]">
        <div className="max-w-5xl mx-auto flex items-center gap-3 px-4 h-12">
          <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-[var(--theme-surface)] transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 text-[var(--theme-text-main)]" />
          </button>
          <h1 className="text-sm font-semibold text-[var(--theme-text-main)] truncate flex-1">{product.name}</h1>
          <button onClick={() => navigate('/cart')} className="relative p-1.5 -mr-1.5" data-testid="cart-icon-btn">
            <ShoppingCart className="w-5 h-5 text-[var(--theme-text-main)]" />
            {totalCartItems > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[var(--theme-primary)] text-white text-[9px] font-bold rounded-full flex items-center justify-center">{totalCartItems}</span>
            )}
          </button>
        </div>
      </header>

      {/* ── Spacer for fixed header ── */}
      {/* Must include the safe-area inset the header itself is shifted down by
          (see `.fixed.top-0` in index.css) — a plain h-12 alone would leave
          content overlapping the header by that inset. */}
      <div className="shrink-0" style={{ height: 'calc(3rem + env(safe-area-inset-top, 0px))' }} />

      {/* ── Main Content ── */}
      <main className="flex-1 overflow-y-auto pb-20">
        <div className="max-w-5xl mx-auto">

          {/* Two-column on md+, single column on mobile */}
          <div className="md:grid md:grid-cols-2 md:gap-6 lg:gap-10">

            {/* LEFT: Image Gallery */}
            <div className="md:sticky md:top-12 md:self-start">
              {/* Image */}
              <div className="relative aspect-square md:aspect-auto md:h-[min(70vh,560px)] bg-[var(--theme-surface)] overflow-hidden md:rounded-b-2xl" data-testid="detail-image-gallery">
                {allImages.length > 0 ? (
                  <ProductImageCarousel product={product} autoPlay={autoPlay} interval={3500} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-6xl font-bold text-[var(--theme-primary)] opacity-10">{product.name?.charAt(0)}</span>
                  </div>
                )}
                {productOos && !comingSoon && <UnavailableBadge />}
                {discount > 0 && (
                  <div className="absolute top-3 left-3 bg-[var(--theme-primary)] text-white text-xs font-bold px-2.5 py-1 rounded-lg">{discount}% OFF</div>
                )}
                {allImages.length > 1 && (
                  <div className="absolute top-3 right-3 bg-black/40 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">{allImages.length} photos</div>
                )}
              </div>

              {/* Thumbnails */}
              {allImages.length > 1 && (
                <div className="flex gap-2 px-4 md:px-0 py-3 overflow-x-auto" data-testid="image-thumbnails" style={{ scrollbarWidth: 'none' }}>
                  {allImages.map((src, i) => (
                    <div key={i} className="w-14 h-14 rounded-lg overflow-hidden border-2 border-[var(--theme-border)] flex-shrink-0 opacity-80 hover:opacity-100 transition-opacity cursor-pointer">
                      <img src={src} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* RIGHT: Product Info */}
            <div className="px-4 md:px-0 md:py-2">
              {/* Name & Category */}
              <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-[var(--theme-text-main)] leading-tight pt-4 md:pt-0" data-testid="product-name">{product.name}</h2>
              {product.category_name && (
                <p className="text-xs md:text-sm text-[var(--theme-text-muted)] mt-1">{product.category_name}{product.sub_category_name ? ` / ${product.sub_category_name}` : ''}</p>
              )}
              <p className="text-xs md:text-sm text-[var(--theme-text-muted)] mt-0.5">{activeVariant ? activeVariant.label : product.unit}</p>

              {/* Price */}
              <div className="flex items-baseline gap-2 mt-3">
                <span className="text-xl md:text-2xl font-bold text-[var(--theme-text-main)]" data-testid="product-price">{curr}{displayPrice}</span>
                {discount > 0 && (
                  <>
                    <span className="text-sm text-[var(--theme-text-muted)] line-through">{curr}{displayMrp}</span>
                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">{discount}% off</span>
                  </>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-[var(--theme-border)] my-4" />

              {/* Variant Selector */}
              {hasVariants && !productOos && (
                <div className="pb-4" data-testid="variant-selector">
                  <p className="text-xs font-semibold text-[var(--theme-text-muted)] mb-2.5 uppercase tracking-wide">Select Variant</p>
                  <div className="flex flex-wrap gap-2">
                    {product.variants.map(v => {
                      const vOos = isVariantUnavailable(v, storeSettings);
                      const isSelected = activeVariant?.label === v.label;
                      const vQty = getItemQuantity(product.id, v.label);
                      return (
                        <button key={v.label} onClick={() => !vOos && setSelectedVariant(v)} disabled={vOos}
                          className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                            vOos ? 'border-gray-200 text-gray-300 cursor-not-allowed line-through bg-gray-50'
                            : isSelected ? 'bg-[var(--theme-primary)] text-white border-[var(--theme-primary)] shadow-sm'
                            : vQty > 0 ? 'border-[var(--theme-primary)] text-[var(--theme-primary)] bg-[var(--theme-primary)]/5'
                            : 'border-[var(--theme-border)] text-[var(--theme-text-main)] hover:border-[var(--theme-primary)]/40'
                          }`}
                          data-testid={`detail-variant-${v.label}`}>
                          {v.label} — {curr}{v.price}
                          {vQty > 0 && <span className="ml-1 text-xs opacity-70">({vQty} in cart)</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Description */}
              {product.description && (
                <div className="pb-4">
                  <p className="text-xs font-semibold text-[var(--theme-text-muted)] mb-1.5 uppercase tracking-wide">Description</p>
                  <p className="text-sm md:text-base text-[var(--theme-text-main)] leading-relaxed">{product.description}</p>
                </div>
              )}

              {/* Desktop-only Add to Cart (inline, not fixed) */}
              {(!productOos || comingSoon) && (
                <div className="hidden md:flex items-center gap-4 py-4 border-t border-[var(--theme-border)]" data-testid="detail-add-inline">
                  {comingSoon ? <ComingSoonButton size="lg" testId="detail-coming-soon" /> : qty === 0 ? (
                    <button onClick={handleAdd}
                      disabled={hasVariants && activeVariant && isVariantUnavailable(activeVariant, storeSettings)}
                      className="px-8 py-3 rounded-xl bg-[var(--theme-primary)] text-white text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-40"
                      data-testid="detail-add-btn">
                      <Plus className="w-4 h-4" /> Add to Cart
                    </button>
                  ) : (
                    <div className="flex items-center gap-1 bg-[var(--theme-primary)] rounded-xl overflow-hidden" data-testid="detail-qty-ctrl">
                      <button onClick={() => updateQuantity(cartKey, qty - 1)} className="px-5 py-3 text-white hover:bg-white/10 transition-colors">
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="px-4 py-3 text-white font-bold text-sm min-w-[44px] text-center">{qty}</span>
                      <button onClick={() => updateQuantity(cartKey, qty + 1)} className="px-5 py-3 text-white hover:bg-white/10 transition-colors">
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div>
                    <span className="text-lg font-bold text-[var(--theme-text-main)]">{curr}{displayPrice}</span>
                    {discount > 0 && <span className="text-xs text-[var(--theme-text-muted)] line-through ml-1.5">{curr}{displayMrp}</span>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Related Products — full width below the grid */}
          {relatedProducts.length > 0 && (
            <div className="px-4 md:px-0 pt-4 pb-4 border-t border-[var(--theme-border)] mt-2">
              <p className="text-xs font-semibold text-[var(--theme-text-muted)] mb-3 uppercase tracking-wide">You might also like</p>
              <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                {relatedProducts.map(rp => {
                  const rpImg = rp.image_url || rp.images?.[0];
                  const rpVariant = rp.variants?.find(v => v.is_default) || rp.variants?.[0];
                  const rpPrice = rpVariant?.price || rp.price;
                  return (
                    <button key={rp.id} onClick={() => navigate(`/product/${rp.id}`)}
                      className="flex-shrink-0 w-28 md:w-36 text-left group" data-testid={`related-${rp.id}`}>
                      <div className="w-28 md:w-36 h-28 md:h-36 rounded-xl overflow-hidden bg-[var(--theme-surface)] mb-1.5">
                        {rpImg ? <img src={rpImg} alt={rp.name} className="w-full h-full object-cover" /> :
                          <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-[var(--theme-primary)] opacity-15">{rp.name.charAt(0)}</div>}
                      </div>
                      <p className="text-xs font-medium text-[var(--theme-text-main)] line-clamp-2 leading-tight">{rp.name}</p>
                      <p className="text-xs font-bold text-[var(--theme-text-main)] mt-0.5">{curr}{rpPrice}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ── Mobile-only Fixed Bottom Bar ── */}
      {(!productOos || comingSoon) && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[var(--theme-bg)] border-t border-[var(--theme-border)] px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]" data-testid="detail-add-bar">
          <div className="flex items-center justify-between gap-3">
            <div>
              <span className="text-lg font-bold text-[var(--theme-text-main)]">{curr}{displayPrice}</span>
              {discount > 0 && <span className="text-xs text-[var(--theme-text-muted)] line-through ml-1.5">{curr}{displayMrp}</span>}
            </div>
            {comingSoon ? <ComingSoonButton size="lg" className="flex-1 max-w-[200px]" testId="detail-coming-soon-mobile" /> : qty === 0 ? (
              <button onClick={handleAdd}
                disabled={hasVariants && activeVariant && isVariantUnavailable(activeVariant, storeSettings)}
                className="flex-1 max-w-[200px] py-2.5 rounded-xl bg-[var(--theme-primary)] text-white text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-40"
                data-testid="detail-add-btn-mobile">
                <Plus className="w-4 h-4" /> Add to Cart
              </button>
            ) : (
              <div className="flex items-center gap-1 bg-[var(--theme-primary)] rounded-xl overflow-hidden" data-testid="detail-qty-ctrl-mobile">
                <button onClick={() => updateQuantity(cartKey, qty - 1)} className="px-4 py-2.5 text-white hover:bg-white/10 transition-colors">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-3 py-2.5 text-white font-bold text-sm min-w-[40px] text-center">{qty}</span>
                <button onClick={() => updateQuantity(cartKey, qty + 1)} className="px-4 py-2.5 text-white hover:bg-white/10 transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
